﻿export type AppIntent =
  | {
      // The multimedia app hands itself a file it found in another Knowledge
      // Area. Files are opened through `openFile` with a `FileRef` (#1619),
      // never through an intent.
      action: 'open-file'
      knowledgeArea: string
      fullPath: string
    }
  | {
      action: 'open-ai-chat'
      knowledgeArea?: string
      prompt?: string
    }
  | {
      action: 'open-knowledge-extractor'
      knowledgeArea: string
    }
  | {
      // Drive 2.0 → Communicator 2.0: use existing personal documents as chat
      // attachments without re-uploading (cdb_webos#211 Phase 1b, #513, #514).
      action: 'attach-to-communicator'
      /** Epoch ms when the intent was set — the consumer discards stale ones
       *  (e.g. an aborted openWindow left it behind), so a later unrelated mount
       *  never silently adopts the file. */
      ts: number
      /** Finished attachment rows (tqs.communicator2_attachments): the brain
       *  injects their text owner-scoped via ChatRequest.attachments, so the
       *  Communicator only has to reference the ids. */
      attachments?: Array<{
        id: string
        file_name: string
        mime_type?: string
        file_size?: number
        char_count?: number
      }>
      /** Versioned personal files (tqs.personal_files, #513) — everything the
       *  brain generated. There is no attachment row to reference yet, and the
       *  endpoint that creates one needs a conversation, which Drive 2.0 does
       *  not have. So the ids travel and the Communicator materialises them
       *  against its own conversation. */
      personalFiles?: Array<{
        id: string
        file_name: string
        file_size?: number
      }>
      /** Which Communicator window asked for these files (#514). Set when the
       *  pick STARTED in a composer, so the files land in the chat the user was
       *  writing in rather than in whichever window consumes first. Absent for
       *  a pick started in Drive 2.0 — then any window may take it. */
      targetWindowId?: string
    }
  | {
      // Communicator 2.0 → Drive 2.0 (#514): the composer's paperclip asks the
      // Drive to let the user pick files for the chat, instead of duplicating
      // the file list in a dialog. Drive answers with `attach-to-communicator`
      // addressed back at `targetWindowId`.
      action: 'pick-files-for-communicator'
      ts: number
      /** The composer that asked — the Drive addresses its answer at it and
       *  focuses it again, so the user lands back where they were writing.
       *
       *  Optional, like on the answer above: on a shell-free route there are no
       *  windows and exactly one Communicator, so the pick is deliberately
       *  unaddressed and whoever consumes it is the right one (#504). Required
       *  here would have forced the composer to suppress the whole feature
       *  under `/chat`. */
      targetWindowId?: string
    }

/** Window event that wakes an already-open Communicator 2.0 to consume a
 *  pending ``attach-to-communicator`` intent (warm path; #211 Phase 1b). */
export const ATTACH_INTENT_EVENT = 'communicator2:attach'

/** The mirror image (#514): wakes an already-open Drive 2.0 to consume a
 *  pending ``pick-files-for-communicator`` intent. */
export const PICK_FILES_INTENT_EVENT = 'drive2:pick-files'

/** How long a hand-off intent stays valid. Long enough for a cold window to
 *  mount, short enough that an intent left behind by an aborted openWindow does
 *  not surface in an unrelated session later. */
export const INTENT_MAX_AGE_MS = 10_000

const memory: Record<string, AppIntent | undefined> = {}

function key(appId: string) {
  return `intent:${appId}`
}

export const appIntentService = {
  set(appId: string, intent: AppIntent) {
    memory[appId] = intent
    try {
      sessionStorage.setItem(key(appId), JSON.stringify(intent))
    } catch {}
  },
  has(appId: string): boolean {
    const k = key(appId)
    if (memory[appId]) return true
    try {
      return sessionStorage.getItem(k) !== null
    } catch {
      return false
    }
  },
  get(appId: string): AppIntent | undefined {
    const k = key(appId)
    let intent = memory[appId]
    if (!intent) {
      try {
        const raw = sessionStorage.getItem(k)
        if (raw) intent = JSON.parse(raw) as AppIntent
      } catch {}
    }
    return intent
  },
  pop(appId: string): AppIntent | undefined {
    const k = key(appId)
    let intent = memory[appId]
    if (!intent) {
      try {
        const raw = sessionStorage.getItem(k)
        if (raw) intent = JSON.parse(raw) as AppIntent
      } catch {}
    }
    memory[appId] = undefined
    try {
      sessionStorage.removeItem(k)
    } catch {}
    return intent
  },
}
